import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { CollapsibleSection } from 'components/CollapsibleSection';
import {
  DATA_ROOM_NDA_SIGNING_STATUS,
  DataRoomNdaSigningStatus,
} from 'router/subrouters/DataRoom/models/DataRoomNDA';
import LockclosedIcon from 'icons/Lockclosed.icon';
import usePastTime from 'hooks/usePastTime';
import DataRoomNdaStatusBadge from '../../../DataRoomNdaStatusBadge';
import { Button } from 'ncoded-component-library';
import BellIcon from 'icons/Bell.icon';
import useSendSignerReminder from '../../hooks';
import TransWithNda from 'components/TransWithNda';
import { SHORT_MAX_CHARS } from 'components/TransWithNda/TransWithNda.component';
import { getFormattedDate, DATE_FORMAT_WITH_FULL_MONTH } from 'dateUtils';

import './DataRoomSignerNdaStatus.styles.scss';

type DataRoomSignerNdaStatusProps = {
  className?: string;
  signingId: string;
  firstName: string;
  sentDate: Date;
  signedDate: Date;
  rejectionReason?: string;
  status: DataRoomNdaSigningStatus;
  envelopeTemplateName: string;
  onSendReminder: (signingId: string) => void;
};

const DataRoomSignerNdaStatus: React.FC<DataRoomSignerNdaStatusProps> = (
  props,
) => {
  const {
    className,
    status,
    signingId,
    sentDate,
    signedDate,
    rejectionReason,
    firstName,
    envelopeTemplateName,
    onSendReminder,
  } = props;

  const classes = classNames(
    'data-room-signer-nda-status',
    'white-panel',
    className,
  );

  const { t } = useTranslation();
  const pastTime = usePastTime();

  const { isLoading, sendReminder } = useSendSignerReminder(
    signingId,
    onSendReminder,
  );

  const TRIGGER: Record<DataRoomNdaSigningStatus, JSX.Element> = useMemo(
    () => ({
      [DATA_ROOM_NDA_SIGNING_STATUS.REJECTED]: (
        <div className="heading">
          <LockclosedIcon />
          {t('NDAstatus')}
          <DataRoomNdaStatusBadge
            status={status}
            rejectionReason={rejectionReason}
            envelopeTemplateName={envelopeTemplateName}
          />
        </div>
      ),
      [DATA_ROOM_NDA_SIGNING_STATUS.PENDING]: (
        <div className="heading">
          <LockclosedIcon />
          {t('NDAstatus')}
          <DataRoomNdaStatusBadge
            status={status}
            sentDate={sentDate}
            envelopeTemplateName={envelopeTemplateName}
          />
        </div>
      ),
      [DATA_ROOM_NDA_SIGNING_STATUS.NDA_EXECUTED]: (
        <div className="heading">
          <LockclosedIcon />
          {t('NDAstatus')}
          <DataRoomNdaStatusBadge
            status={status}
            signedDate={signedDate}
            envelopeTemplateName={envelopeTemplateName}
          />
        </div>
      ),
      [DATA_ROOM_NDA_SIGNING_STATUS.NOT_SENT]: (
        <div className="heading">
          <LockclosedIcon />
          {t('NDAstatus')}
          <DataRoomNdaStatusBadge
            status={status}
            envelopeTemplateName={envelopeTemplateName}
          />
        </div>
      ),
      [DATA_ROOM_NDA_SIGNING_STATUS.COUNTER_SIGNATURE_PENDING]: (
        <div className="heading">
          <LockclosedIcon />
          {t('NDAstatus')}
          <DataRoomNdaStatusBadge
            status={status}
            envelopeTemplateName={envelopeTemplateName}
          />
        </div>
      ),
    }),
    [sentDate, status, signedDate, rejectionReason, envelopeTemplateName, t],
  );

  const CONTENT: Record<DataRoomNdaSigningStatus, JSX.Element> = useMemo(
    () => ({
      [DATA_ROOM_NDA_SIGNING_STATUS.REJECTED]: (
        <div className="content">
          <TransWithNda
            i18nKey="userRejectedNDA"
            maxChars={SHORT_MAX_CHARS}
            envelopeTemplateName={envelopeTemplateName}
            values={{
              name: firstName,
              rejectionReason: rejectionReason || '/',
            }}
          />
        </div>
      ),
      [DATA_ROOM_NDA_SIGNING_STATUS.NDA_EXECUTED]: (
        <div className="content success">
          <TransWithNda
            i18nKey={signedDate ? 'NDAsignedWithDate' : 'NDAsigned'}
            maxChars={SHORT_MAX_CHARS}
            envelopeTemplateName={envelopeTemplateName}
            values={{
              firstName,
              date: signedDate
                ? getFormattedDate(signedDate, DATE_FORMAT_WITH_FULL_MONTH)
                : null,
            }}
          />
        </div>
      ),
      [DATA_ROOM_NDA_SIGNING_STATUS.PENDING]: (
        <div className="content">
          <TransWithNda
            i18nKey="NDAsent"
            maxChars={SHORT_MAX_CHARS}
            envelopeTemplateName={envelopeTemplateName}
            values={{
              firstName,
              date: sentDate ? pastTime(sentDate) : null,
            }}
          />

          <Button
            icon={BellIcon}
            variant="outline"
            disabled={isLoading}
            onClick={sendReminder}
          >
            {t('sendReminder')}
          </Button>
        </div>
      ),
      [DATA_ROOM_NDA_SIGNING_STATUS.NOT_SENT]: (
        <div className="content">
          <TransWithNda
            i18nKey="NDAnotSent"
            maxChars={SHORT_MAX_CHARS}
            envelopeTemplateName={envelopeTemplateName}
          />
        </div>
      ),
      [DATA_ROOM_NDA_SIGNING_STATUS.COUNTER_SIGNATURE_PENDING]: (
        <div className="content">
          <TransWithNda
            i18nKey="NDAcounterSignaturePending"
            maxChars={SHORT_MAX_CHARS}
            envelopeTemplateName={envelopeTemplateName}
            values={{ firstName }}
          />
        </div>
      ),
    }),
    [
      firstName,
      rejectionReason,
      signedDate,
      sentDate,
      isLoading,
      envelopeTemplateName,
      t,
      pastTime,
      sendReminder,
    ],
  );

  return (
    <CollapsibleSection
      defaultOpen
      className={classes}
      trigger={TRIGGER[status]}
    >
      {CONTENT[status]}
    </CollapsibleSection>
  );
};

export default DataRoomSignerNdaStatus;
